import React, { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-hook-inview';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { Accessory } from '../../../@types/item';
import { track as _track, mapVehicleData } from '../../../actions/tracking';
import { prettify } from '../../../helpers/prices';
import { cdn } from '../../../helpers/urls';

import { bindActionCreators } from '@reduxjs/toolkit';
import { getItem } from '../../../selectors/item';
import AccessoryCard from '../../../styled-components/components/AccessoryCard';

const fallbackImage = '/images/placeholders/placeholder_product_card.jpg';

interface AccessoryItemProps {
  accessory: Accessory;
  onClick: (accessory: Accessory) => void;
}

const AccessoryItem = ({ accessory, onClick }: AccessoryItemProps) => {
  const { t } = useTranslation(['common']);
  const dispatch = useDispatch();
  const item = useSelector(getItem);
  const track = bindActionCreators(_track, dispatch);

  const spreadable: { [key: string]: undefined | string | { [key: string]: undefined | string } } =
    {
      information: {
        id: accessory.id,
        label: accessory.name,
        context: 'productOverview',
      },
    };

  const _onClick = useCallback(() => {
    onClick(accessory);

    track('accessoryInformationClick', {
      item: mapVehicleData(item),
      spreadable,
    });
  }, [accessory, onClick]);
  const trackLock = useRef(false);

  const [ref, isVisible] = useInView({
    threshold: 1,
  });

  useEffect(() => {
    if (isVisible && !trackLock.current) {
      trackLock.current = true;
      track('accessoryExposure', {
        item: mapVehicleData(item),
        spreadable,
      });
    }
  }, [isVisible]);
  return (
    <AccessoryCard
      ref={ref}
      image={{
        src: accessory?.media?.[0]?.url ? `${accessory.media[0].url}?w=415&q=72` : fallbackImage,
        srcSet: accessory?.media?.[0]?.url ? `${accessory.media[0].url}?w=830&q=72 2x` : undefined,
        alt: accessory.name,
      }}
      logo={
        accessory.logoUrl
          ? {
              src: `${cdn(accessory.logoUrl, '48x48')}&crop=fit`,
              srcSet: `${cdn(accessory.logoUrl, '96x96')}&crop=fit 2x`,
              alt: accessory.name,
            }
          : undefined
      }
      heading={accessory.name}
      price={prettify(accessory.price, 'kr')}
      salePrice={accessory.salePrice ? prettify(accessory.salePrice, 'kr') : undefined}
      preamble={accessory.shortDescription}
      readMoreBtn={{
        label: t('readMore', { ns: 'common' }),
        title: t('Read more about this', { ns: 'common', name: accessory.name }),
        onClick: _onClick,
      }}
    />
  );
};

export default AccessoryItem;
